/**
 * Utility for exporting raw student feedback responses to an Excel (.xlsx) spreadsheet.
 * Uses the xlsx library.
 */

import * as XLSX from 'xlsx';

function parseAnswers(rawAnswers) {
  if (!rawAnswers) return {};
  if (typeof rawAnswers === 'object' && !Array.isArray(rawAnswers)) {
    return rawAnswers;
  }
  if (typeof rawAnswers === 'string') {
    try {
      const parsed = JSON.parse(rawAnswers);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
}

const RATING_DESCRIPTIONS = {
  1: 'Needs Improvement',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent'
};

function formatAnswerValue(val, type) {
  if (val === undefined || val === null || val === '') {
    return '';
  }
  const numVal = Number(val);
  const isRating = (
    type === 'rating' ||
    (typeof val === 'number' && val >= 1 && val <= 5) ||
    (typeof val === 'string' && val.length === 1 && !isNaN(numVal) && numVal >= 1 && numVal <= 5)
  );

  if (isRating && numVal >= 1 && numVal <= 5) {
    const roundedRating = Math.round(numVal);
    const desc = RATING_DESCRIPTIONS[roundedRating];
    return desc ? `${roundedRating} / 5 (${desc})` : `${roundedRating} / 5`;
  }
  if (Array.isArray(val)) {
    return val.join(', ');
  }
  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No';
  }
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch (_) {
      return String(val);
    }
  }
  return String(val);
}

export async function downloadRawFeedbackExcel({
  feedbacks = [],
  reportType = 'lms', // 'lms' | 'platform'
  universityName = 'All',
  courseName = 'All',
  courseForms = [],
  platformForms = [],
  lmsSchema = null
}) {
  if (!feedbacks || feedbacks.length === 0) {
    throw new Error('No feedback submissions available to export.');
  }

  const isLms = reportType === 'lms';

  // 1. Build a master question lookup dictionary across all available form definitions
  const masterQuestionsDict = {};
  const allAvailableForms = [...(courseForms || []), ...(platformForms || [])];

  allAvailableForms.forEach(form => {
    (form.questions || []).forEach(q => {
      if (q && q.id && !masterQuestionsDict[q.id]) {
        masterQuestionsDict[q.id] = q;
      }
    });
  });

  if (lmsSchema) {
    ['module_feedback', 'platform_feedback'].forEach(sec => {
      const secQs = lmsSchema[sec];
      if (Array.isArray(secQs)) {
        secQs.forEach(q => {
          if (q && q.id && !masterQuestionsDict[q.id]) {
            masterQuestionsDict[q.id] = q;
          }
        });
      }
    });
  }

  // 2. Determine which forms were actually used in the exported feedbacks
  const usedFormIds = new Set(
    feedbacks.map(f => f.form_id || f.course_id).filter(Boolean)
  );

  // Forms that were submitted by at least one student in this export
  const relevantForms = allAvailableForms.filter(f => usedFormIds.has(f.id));

  // Build question columns dynamically:
  // Each column represents a distinct question prompt.
  // Questions with identical text across forms are mapped to the same column so all
  // student answers neatly appear under that question.
  const questionColumns = []; // Array of { header, qIds: Set, type }
  const seenNormHeaders = new Map(); // normalizedText -> columnObj

  const registerQuestion = (qId, rawText, qType) => {
    if (!qId) return;
    const cleanHeader = (rawText || qId).trim();
    const normKey = cleanHeader.toLowerCase();

    if (seenNormHeaders.has(normKey)) {
      const existingCol = seenNormHeaders.get(normKey);
      existingCol.qIds.add(qId);
      if (qType && qType !== 'text') {
        existingCol.type = qType;
      }
    } else {
      const newCol = {
        header: cleanHeader,
        qIds: new Set([qId]),
        type: qType || 'text'
      };
      seenNormHeaders.set(normKey, newCol);
      questionColumns.push(newCol);
    }
  };

  // First, add all questions from the forms that students actually submitted
  relevantForms.forEach(form => {
    (form.questions || []).forEach(q => {
      if (q && q.id) {
        registerQuestion(q.id, q.text || q.question || q.id, q.type);
      }
    });
  });

  // Next, inspect student answer keys to ensure all answered questions are captured
  feedbacks.forEach(item => {
    let answersObj = isLms ? parseAnswers(item.module_feedback) : parseAnswers(item.platform_feedback);
    if (Object.keys(answersObj).length === 0) {
      const altObj = isLms ? parseAnswers(item.platform_feedback) : parseAnswers(item.module_feedback);
      if (Object.keys(altObj).length > 0) {
        answersObj = altObj;
      }
    }

    Object.keys(answersObj).forEach(key => {
      const masterQ = masterQuestionsDict[key];
      const val = answersObj[key];
      const guessedType = (typeof val === 'number' && val >= 1 && val <= 5) ? 'rating' : (masterQ?.type || 'text');
      const text = masterQ?.text || masterQ?.question || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      registerQuestion(key, text, guessedType);
    });
  });

  // If no questions were found from used forms, fallback to all forms of this report type
  if (questionColumns.length === 0) {
    const fallbackForms = isLms ? (courseForms || []) : (platformForms || []);
    fallbackForms.forEach(form => {
      (form.questions || []).forEach(q => {
        if (q && q.id) {
          registerQuestion(q.id, q.text || q.question || q.id, q.type);
        }
      });
    });
  }

  // 3. Build data rows for student submissions
  const exportRows = feedbacks.map((item, index) => {
    const studentName = item.student_name || (item.student_id ? `Student #${item.student_id}` : 'Unknown');
    const uniName = item.university_name || 'Unknown College';
    const batchName = item.batch_name || 'General Batch';
    const courseOrForm = isLms
      ? (item.course_name ? `${item.course_name} (${item.course_id})` : (item.course_id || 'Course'))
      : (item.form_title ? `Form: ${item.form_title}` : (item.course_name || 'Platform Survey'));
    const submissionDate = item.created_at
      ? new Date(item.created_at).toLocaleDateString('en-GB')
      : '—';
    const usn = item.usn || '';
    const email = item.student_email || '';

    let answersObj = isLms ? parseAnswers(item.module_feedback) : parseAnswers(item.platform_feedback);
    if (Object.keys(answersObj).length === 0) {
      const altObj = isLms ? parseAnswers(item.platform_feedback) : parseAnswers(item.module_feedback);
      if (Object.keys(altObj).length > 0) {
        answersObj = altObj;
      }
    }

    const row = {
      'Sl No.': index + 1,
      'Submission Date': submissionDate,
      'Student Name': studentName,
      'USN': usn,
      'Student Email': email,
      'University': uniName,
      'Course / Form': courseOrForm,
      'Batch': batchName
    };

    questionColumns.forEach(col => {
      // Find the first matching value from any of the column's associated question IDs
      let answerVal = undefined;
      for (const qid of col.qIds) {
        if (answersObj[qid] !== undefined && answersObj[qid] !== null && answersObj[qid] !== '') {
          answerVal = answersObj[qid];
          break;
        }
      }
      row[col.header] = formatAnswerValue(answerVal, col.type);
    });

    return row;
  });

  // 4. Construct sheet & configure styling/widths
  const worksheet = XLSX.utils.json_to_sheet(exportRows);

  const colKeys = Object.keys(exportRows[0] || {});
  const colWidths = colKeys.map(key => {
    let maxLen = key.length;
    exportRows.forEach(r => {
      const val = r[key];
      if (val !== undefined && val !== null) {
        const len = String(val).length;
        if (len > maxLen) maxLen = len;
      }
    });
    return { wch: Math.min(Math.max(maxLen + 3, 12), 60) };
  });
  worksheet['!cols'] = colWidths;

  if (worksheet['!ref']) {
    worksheet['!autofilter'] = { ref: worksheet['!ref'] };
  }

  const workbook = XLSX.utils.book_new();
  const sheetName = isLms ? 'LMS Raw Feedback' : 'Platform Raw Feedback';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 5. Add Summary overview sheet
  const uniqueUniversities = Array.from(new Set(feedbacks.map(f => f.university_name).filter(Boolean)));
  const uniqueBatches = Array.from(new Set(feedbacks.map(f => f.batch_name).filter(Boolean)));
  const summaryRows = [
    { 'Metric': 'Report Type', 'Details': isLms ? 'LMS Course Feedback' : 'Platform System Survey' },
    { 'Metric': 'Export Date', 'Details': new Date().toLocaleString('en-GB') },
    { 'Metric': 'College / University Filter', 'Details': universityName && universityName !== 'All' ? universityName : 'All Participating Colleges' },
    { 'Metric': 'Course / Form Scope', 'Details': isLms ? (courseName && courseName !== 'All' ? courseName : 'All LMS Courses') : 'Platform Survey' },
    { 'Metric': 'Total Submissions', 'Details': feedbacks.length },
    { 'Metric': 'Total Colleges Represented', 'Details': uniqueUniversities.length },
    { 'Metric': 'Total Batches Represented', 'Details': uniqueBatches.length }
  ];
  const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
  summarySheet['!cols'] = [{ wch: 28 }, { wch: 45 }];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Report Summary');

  // 6. Generate filename and trigger download
  const uniSlug = universityName && universityName !== 'All'
    ? `_${String(universityName).replace(/[^a-zA-Z0-9_-]/g, '_')}`
    : '_All_Colleges';
  const courseSlug = isLms && courseName && courseName !== 'All'
    ? `_${String(courseName).replace(/[^a-zA-Z0-9_-]/g, '_')}`
    : '';
  const filename = isLms
    ? `LMS_Raw_Feedback_Report${uniSlug}${courseSlug}.xlsx`
    : `Platform_Raw_Feedback_Report${uniSlug}.xlsx`;

  XLSX.writeFile(workbook, filename);
}
